/**
 * Theme constants for prescription ingestion module
 * Centralized colors, spacing, and design tokens
 */

export const COLORS = {
    // Primary colors
    primary: "#667eea",
    secondary: "#764ba2",
    orange: "#ff9800", // Orange color matching the top navigation bar

    // Accent colors
    pink: "#f687b3",
    blue: "#4299e1",
    purple: "#9f7aea",

    // Status colors
    success: "#48bb78",
    warning: "#ed8936",
    error: "#f44336",
    info: "#2196f3",

    // Neutral colors
    white: "#ffffff",
    gray200: "#e0e0e0",

    // Text colors
    textPrimary: "#2d3748",
    textSecondary: "#718096",
    textDark: "#232f3e",

    // Background colors
    background: "#f5f7fa",
    bgLight: "#f5f5f5",

    // UI colors
    iconGray: "#546e7a",
    borderGray: "#e0e0e0",
} as const;

export const SPACING = {
    xs: 8,
    small: "8px",
    sm: 16,
    md: 24,
    medium: "16px",
    lg: 32,
    large: "24px",
    xl: 40,
    xlarge: "40px",
} as const;

export const TRANSITIONS = {
    default: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    fast: "all 0.2s ease-in-out",
} as const;

export const BORDER_RADIUS = {
    sm: 8,
    md: 12,
    medium: "12px",
    lg: 16,
    large: "16px",
    circle: "50%",
} as const;

export const SHADOWS = {
    xsmall: "0 1px 3px rgba(0,0,0,0.1)",
    sm: "0 2px 8px rgba(0,0,0,0.1)",
    small: "0 2px 8px rgba(0,0,0,0.1)",
    md: "0 4px 12px rgba(0,0,0,0.15)",
    lg: "0 8px 16px rgba(0,0,0,0.2)",
    large: "0 10px 40px rgba(102, 126, 234, 0.2)",
    hover: "0 12px 24px rgba(102, 126, 234, 0.15)",
    colored: (color: string) => `0 4px 6px ${color}25`,
} as const;


/**
 * Button styles for consistent button appearance
 */
export const BUTTON_STYLES = {
    primary: {
        textTransform: "none" as const,
        fontWeight: 600,
        borderRadius: 8,
        padding: "10px 24px",
        boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
        transition: TRANSITIONS.default,
        "&:hover": {
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
            transform: "translateY(-1px)",
        },
    },
    secondary: {
        textTransform: "none" as const,
        fontWeight: 600,
        borderRadius: 8,
        padding: "10px 24px",
    },
    large: {
        fontSize: 16,
        padding: "12px 32px",
    },
    withIcon: {
        display: "flex",
        alignItems: "center",
        gap: 8,
    },
    // Orange-outline style for the "next step" action buttons in the NewRx
    // Operations flow (Approve / Create Order steps).
    nextStep: {
        textTransform: "none" as const,
        fontWeight: 600,
        borderColor: COLORS.orange,
        color: COLORS.orange,
    },
} as const;
